import {useState, useEffect, useMemo} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import DataTable from "@/components/DataTable";
import ChoiceChips from "@/components/ChoiceChips";
import EmptyData from "@/components/EmptyData";
import StatusBadge from "@/components/StatusBadge";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";

import {getSessionById} from "@/utils/gpsSessions";
import {GPS_METRICS, acwrStatus} from "@/utils/gpsParser";
import {getAcwrConfig} from "@/utils/gpsConfig";

import "./index.scss";
import {COLORS} from "@/style/utils";

// ── Constants ─────────────────────────────────────────────────────────────────

const CHART_METRICS = GPS_METRICS.filter((m) =>
	["distTotal", "maxSpeed", "numAcc", "numDec", "distOver25", "acwr"].includes(
		m.key,
	),
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (val, decimals) => {
	if (val == null || isNaN(val)) return "—";
	return Number(val).toFixed(decimals ?? 0);
};

const isTeamRow = (row) => {
	const name = (row.playerLabel ?? row.playerName ?? "").toLowerCase();
	return (
		name.includes("squadra") ||
		name.includes("totale") ||
		name.includes("team total") ||
		name.includes("all players")
	);
};

// ── Sub-components ────────────────────────────────────────────────────────────

function AcwrBadge({value, config}) {
	const s = acwrStatus(value, config);
	if (!s) return "—";
	return <StatusBadge tone={s.tone} label={fmt(value, 2) + " · " + s.label} />;
}

const metricLabel = (m) => m.label + (m.unit ? ` (${m.unit})` : "");

// ── Main page ─────────────────────────────────────────────────────────────────

function GPSSessionPage() {
	const navigate = useNavigate();
	const {id} = useParams();

	const [session, setSession] = useState(null);
	const [loading, setLoading] = useState(true);
	const [activeMetric, setActiveMetric] = useState("distTotal");
	const acwrConfig = useMemo(() => getAcwrConfig(), []);

	useEffect(() => {
		setSession(getSessionById(id));
		setLoading(false);
	}, [id]);

	// ── Derived data ──────────────────────────────────────────────────────────

	const playerRows = useMemo(
		() => (session?.rows ?? []).filter((r) => !isTeamRow(r)),
		[session],
	);

	const teamRow = useMemo(
		() => (session?.rows ?? []).find(isTeamRow) ?? null,
		[session],
	);

	// Columns with ≥2 individual player values > 0
	const visibleMetrics = useMemo(
		() =>
			GPS_METRICS.filter(
				(m) =>
					playerRows.filter(
						(r) => r[m.key] != null && !isNaN(r[m.key]) && r[m.key] > 0,
					).length >= 2,
			),
		[playerRows],
	);

	// Per-metric min/max (player rows only) for heat-map coloring
	const metricRanges = useMemo(() => {
		const ranges = {};
		visibleMetrics.forEach((m) => {
			const vals = playerRows
				.map((r) => r[m.key])
				.filter((v) => v != null && !isNaN(v) && v > 0);
			if (vals.length > 1) {
				const min = Math.min(...vals);
				const max = Math.max(...vals);
				if (max > min) ranges[m.key] = {min, max};
			}
		});
		return ranges;
	}, [playerRows, visibleMetrics]);

	const activeMetricDef = useMemo(
		() => GPS_METRICS.find((m) => m.key === activeMetric) ?? GPS_METRICS[0],
		[activeMetric],
	);

	const chartData = useMemo(() => {
		if (!session) return [];
		return [...(session.rows ?? [])]
			.filter(
				(r) =>
					r[activeMetric] != null &&
					!isNaN(r[activeMetric]) &&
					r[activeMetric] > 0,
			)
			.sort((a, b) => (b[activeMetric] ?? 0) - (a[activeMetric] ?? 0))
			.map((r) => ({
				name: r.playerLabel ?? r.playerName ?? "—",
				value: Number(r[activeMetric]),
			}));
	}, [session, activeMetric]);

	// ── Cell coloring ─────────────────────────────────────────────────────────

	const cellTone = (metricKey, value) => {
		if (metricKey === "acwr" || value == null || isNaN(value) || value <= 0)
			return null;
		const range = metricRanges[metricKey];
		if (!range) return null;
		const ratio = (value - range.min) / (range.max - range.min);
		if (ratio >= 0.66) return "high";
		if (ratio >= 0.33) return "mid";
		return "low";
	};

	// ── Render helpers ────────────────────────────────────────────────────────

	const sessionTitle = session
		? session.label ||
			(session.sessionType === "match" ? "Partita" : "Allenamento")
		: "—";

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/gps"),
			color: "info",
		},
	];

	const columns = [
		{
			key: "name",
			label: "Giocatore",
			render: (row) => row.playerLabel ?? row.playerName ?? "—",
			sortValue: (row) => row.playerLabel ?? row.playerName ?? "",
		},
		...visibleMetrics.map((m) => ({
			key: m.key,
			label: metricLabel(m),
			align: m.key === "acwr" ? undefined : "end",
			sortValue: (row) => row[m.key] ?? null,
			render: (row) => {
				if (m.key === "acwr")
					return <AcwrBadge value={row[m.key]} config={acwrConfig} />;
				const tone = row.isTeam ? null : cellTone(m.key, row[m.key]);
				return (
					<span className={"gps-cell" + (tone ? " gps-cell--" + tone : "")}>
						{fmt(row[m.key], m.decimals)}
					</span>
				);
			},
		})),
	];

	const breadcrumbs = (
		<Breadcrumbs
			elements={[{label: "GPS", path: "/gps"}]}
			current={sessionTitle}
			actions={<MainActions actions={mainActions} />}
		/>
	);

	if (!loading && !session) {
		return (
			<Page>
				{breadcrumbs}
				<EmptyData>Sessione non trovata.</EmptyData>
			</Page>
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			{breadcrumbs}

			{session && (
				<div className="gps-session">
					<VLFCard
						content={
							<div className="gps-session-info">
								<div
									className={
										"gps-session-info__icon gps-session-info__icon--" +
										session.sessionType
									}
								>
									{session.sessionType === "match" ? (
										<SportsSoccerIcon />
									) : (
										<FitnessCenterIcon />
									)}
								</div>
								<div>
									<div className="gps-session-info__title">{sessionTitle}</div>
									<div className="gps-session-info__meta">
										{session.date
											? moment(session.date).format("dddd D MMMM YYYY")
											: "—"}
										{session.time ? " · " + session.time : ""}
										{" · "}
										{playerRows.length} giocatori
									</div>
								</div>
							</div>
						}
					/>

					<VLFCard
						title="Confronto tra i giocatori"
						content={
							<>
								<ChoiceChips
									options={CHART_METRICS.map((m) => ({
										value: m.key,
										label: metricLabel(m),
									}))}
									value={activeMetric}
									onChange={setActiveMetric}
								/>
								{chartData.length > 0 ? (
									<div className="gps-chart">
										<ResponsiveContainer width="100%" height={280}>
											<BarChart
												data={chartData}
												margin={{top: 4, right: 16, left: 0, bottom: 20}}
											>
												<CartesianGrid
													strokeDasharray="3 3"
													stroke={COLORS.border}
												/>
												<XAxis
													dataKey="name"
													tick={{fontSize: 11, fill: COLORS.textSecondary}}
													interval={0}
													angle={-35}
													textAnchor="end"
													height={80}
												/>
												<YAxis
													tick={{fontSize: 11, fill: COLORS.textSecondary}}
													width={48}
												/>
												<Tooltip
													formatter={(v) => [
														fmt(v, activeMetricDef.decimals) +
															(activeMetricDef.unit
																? " " + activeMetricDef.unit
																: ""),
														activeMetricDef.label,
													]}
													contentStyle={{
														borderRadius: 8,
														border: "1px solid " + COLORS.border,
														background: COLORS.surface,
														fontSize: 12,
													}}
												/>
												<Bar
													dataKey="value"
													fill={COLORS.primary}
													radius={[4, 4, 0, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<EmptyData>Nessun dato per questa metrica.</EmptyData>
								)}
							</>
						}
					/>

					<VLFCard
						title="Dati per giocatore"
						content={
							<DataTable
								columns={columns}
								rows={[
									...playerRows.map((r, i) => ({...r, id: i})),
									...(teamRow ? [{...teamRow, id: "team", isTeam: true}] : []),
								]}
								empty="Nessun giocatore in questa sessione."
							/>
						}
					/>
				</div>
			)}
		</Page>
	);
}

export default GPSSessionPage;
